import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * SiblingDiscountCard — Admin toggles for the automatic sibling discount
 * that appears as a negative line item on grouped payment intents.
 *
 * Rules supported:
 *   • percent_per_additional_child (default) — % off each extra child
 *   • flat_per_additional_child             — £ off each extra child
 *   • percent_whole_basket                  — % off the whole basket
 */
export function SiblingDiscountCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [rule, setRule] = useState('percent_per_additional_child');
  const [value, setValue] = useState(10);

  useEffect(() => {
    (async () => {
      try {
        const org = await api.getMyOrganization();
        const sd = (org.settings || {}).sibling_discount || {};
        setEnabled(!!sd.enabled);
        setRule(sd.rule || 'percent_per_additional_child');
        setValue(Number(sd.value ?? 10));
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateMyOrganization({
        settings: {
          sibling_discount: {
            enabled,
            rule,
            value: Number(value) || 0,
          },
        },
      });
      toast.success('Sibling discount saved');
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const unitSuffix = rule.startsWith('percent') ? '%' : '£';

  return (
    <div className="card" data-testid="sibling-discount-card"
         style={{ marginBottom: '1.25rem' }}>
      <h3 style={{ margin: '0 0 0.4rem', fontSize: '1rem' }}>👨‍👩‍👧‍👦 Sibling discount</h3>
      <p style={{ margin: '0 0 0.9rem', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
        Automatically applied as a negative line item when a parent books for
        2+ children in one basket. Parents see the saving before they pay.
      </p>
      {loading ? (
        <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>Loading…</div>
      ) : (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', marginBottom: '0.85rem' }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
              data-testid="sibling-discount-enabled" />
            Enable sibling discount
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 130px', gap: '0.75rem', alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', display: 'block', marginBottom: 4 }}>
                Rule
              </label>
              <select value={rule} onChange={(e) => setRule(e.target.value)}
                disabled={!enabled}
                data-testid="sibling-discount-rule"
                style={{ width: '100%', padding: '0.4rem 0.55rem', fontSize: '0.88rem', borderRadius: 6, border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))' }}>
                <option value="percent_per_additional_child">% off each additional child</option>
                <option value="flat_per_additional_child">£ off each additional child</option>
                <option value="percent_whole_basket">% off whole basket</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', display: 'block', marginBottom: 4 }}>
                Value ({unitSuffix})
              </label>
              <input type="number" min="0" step="0.5" value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={!enabled}
                data-testid="sibling-discount-value"
                style={{ width: '100%', padding: '0.4rem 0.55rem', fontSize: '0.88rem', borderRadius: 6, border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))' }} />
            </div>
          </div>
          <button type="button" className="btn btn-primary" onClick={save}
            disabled={saving}
            data-testid="sibling-discount-save"
            style={{ marginTop: '0.85rem' }}>
            {saving ? 'Saving…' : 'Save sibling discount'}
          </button>
        </>
      )}
    </div>
  );
}

export default SiblingDiscountCard;
